// ── Sidebar Navigation ──

import { t, translateElements } from '../lib/i18n.js';
import { api } from '../api.js';
import { getUser } from '../auth.js';
import { isSystemAdmin } from '../lib/permissions.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../utils.js';
import { openCreateProjectModal } from './create-project-modal.js';
import { wordmarkHtml, brandDeltaGlyph } from '../lib/brand.js';

// ── Icons (inline SVG, stroke-based) ──

const ICONS = {
  dashboard: `<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>`,
  board: `<svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>`,
  kanban: `<svg viewBox="0 0 24 24"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="12" rx="1"/><rect x="17" y="3" width="5" height="15" rx="1"/></svg>`,
  epics: `<svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>`,
  backlog: `<svg viewBox="0 0 24 24"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/></svg>`,
  planning: `<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/><line x1="5" y1="7" x2="8" y2="7"/><line x1="5" y1="10" x2="8" y2="10"/><line x1="16" y1="7" x2="19" y2="7"/></svg>`,
  insights: `<svg viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`,
  server: `<svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>`,
  settings: `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>`,
  folder: `<svg viewBox="0 0 24 24"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>`,
  admin: `<svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  chevronLeft: `<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>`,
  chevronRight: `<svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>`,
  chevronDown: `<svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>`,
  externalLink: `<svg viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
};

function icon(name) {
  return `<span class="sidebar-icon">${ICONS[name] || ''}</span>`;
}

// ── State ──

const ALL_PROJECTS_SCOPE = 'all';
const SCOPE_NEW = '__new__';

let cachedProjects = null;
let currentProjectId = null;
let currentView = null;
let isCollapsed = localStorage.getItem('tipatask_sidebar_collapsed') === 'true';
let isMobileOpen = false;

// Workspace scope popover (custom listbox replacing the old native <select>, C1335)
let scopePanel = null;
let scopeOutsideHandler = null;
let scopeResizeHandler = null;
let scopeScrollHandler = null;
let scopeActiveIndex = -1;

// ── Render ──

function renderDashboardLink() {
  return `<a href="#/" class="sidebar-link" data-view="dashboard">
    ${icon('dashboard')}
    <span class="sidebar-label" data-i18n-text="nav.dashboard">${t('nav.dashboard')}</span>
  </a>`;
}

function buildWorkspaceSection() {
  return `<nav class="sidebar-nav" id="sidebar-workspace-section">
    <div class="sidebar-section-label">${icon('dashboard')}<span class="sidebar-label" data-i18n-text="nav.workspace">${t('nav.workspace')}</span></div>
    ${renderDashboardLink()}
    <div class="sidebar-scope-row">
      ${icon('folder')}
      <button class="sidebar-scope-btn" id="sidebar-scope-btn" type="button"
        aria-haspopup="listbox" aria-expanded="false" data-i18n-aria-label="sidebar.scope" aria-label="${t('sidebar.scope')}">
        <span class="sidebar-scope-btn-label" id="sidebar-scope-label">${escapeHtml(scopeLabelText())}</span>
        ${icon('chevronDown')}
      </button>
    </div>
    ${renderAdminLink()}
  </nav>`;
}

// System admins only. Rendered hidden for everyone; updateSidebarContext() reveals it from
// the cached user on every navigation, so it follows a login or a changed admin flag
// without re-rendering the shell.
function renderAdminLink() {
  return `<a href="#/admin" class="sidebar-link" data-view="admin" id="sidebar-link-admin" hidden>
    ${icon('admin')}
    <span class="sidebar-label" data-i18n-text="nav.admin">${t('nav.admin')}</span>
  </a>`;
}

function renderProjectSection() {
  return `<nav class="sidebar-nav" id="sidebar-project-section" hidden>
    <div class="sidebar-section-label">${icon('board')}<span class="sidebar-label" data-i18n-text="nav.project">${t('nav.project')}</span></div>
    <a class="sidebar-link disabled" data-view="board" id="sidebar-link-board">
      ${icon('board')}
      <span class="sidebar-label" data-i18n-text="nav.board">${t('nav.board')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="epics" id="sidebar-link-epics">
      ${icon('epics')}
      <span class="sidebar-label" data-i18n-text="nav.epics">${t('nav.epics')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="backlog" id="sidebar-link-backlog">
      ${icon('backlog')}
      <span class="sidebar-label" data-i18n-text="nav.backlog">${t('nav.backlog')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="planning" id="sidebar-link-planning">
      ${icon('planning')}
      <span class="sidebar-label" data-i18n-text="nav.planning">${t('nav.planning')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="insights" id="sidebar-link-insights">
      ${icon('insights')}
      <span class="sidebar-label" data-i18n-text="nav.insights">${t('nav.insights')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="server-agent" id="sidebar-link-server-agent">
      ${icon('server')}
      <span class="sidebar-label" data-i18n-text="nav.serverAgent">${t('nav.serverAgent')}</span>
    </a>
    <a class="sidebar-link disabled" data-view="settings" id="sidebar-link-settings" hidden>
      ${icon('settings')}
      <span class="sidebar-label" data-i18n-text="nav.settings">${t('nav.settings')}</span>
    </a>
  </nav>`;
}

export function renderSidebar() {
  return `<aside class="sidebar ${isCollapsed ? 'collapsed' : ''}" id="sidebar">
    <div class="sidebar-brand brand-lockup" role="img" aria-label="TipΔTask">
      <span class="sidebar-label">${wordmarkHtml()}</span>
      ${brandDeltaGlyph({ className: 'brand-delta-rail' })}
    </div>

    <div class="sidebar-scroll">
      ${buildWorkspaceSection()}
      ${renderProjectSection()}
    </div>

    <div class="sidebar-footer-row">
      <button class="sidebar-toggle" id="sidebar-toggle" type="button" data-i18n-title="sidebar.toggle" title="${t('sidebar.toggle')}"
        aria-label="${t(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse')}"
        aria-expanded="${isCollapsed ? 'false' : 'true'}">
        ${icon(isCollapsed ? 'chevronRight' : 'chevronLeft')}
      </button>
      <span class="sidebar-copyright">© 2026 Tipatask</span>
    </div>
  </aside>`;
}

function updateSidebarToggle(toggleBtn) {
  if (!toggleBtn) return;
  toggleBtn.innerHTML = icon(isCollapsed ? 'chevronRight' : 'chevronLeft');
  toggleBtn.title = t(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse');
  toggleBtn.setAttribute('aria-label', t(isCollapsed ? 'sidebar.expand' : 'sidebar.collapse'));
  toggleBtn.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
}

// Keep one toggle button in one shell slot. This makes future sidebar refreshes safe: an
// existing control is moved into the slot instead of being recreated, and collapse state only
// updates its attributes and icon.
export function ensureStableSidebarToggle(sidebar = document.getElementById('sidebar')) {
  if (!sidebar) return null;

  let shell = sidebar.querySelector('.sidebar-footer-row');
  if (!shell) {
    shell = document.createElement('div');
    shell.className = 'sidebar-footer-row';
    sidebar.appendChild(shell);
  }

  let toggleBtn = sidebar.querySelector('#sidebar-toggle');
  if (!toggleBtn) {
    toggleBtn = document.createElement('button');
    toggleBtn.className = 'sidebar-toggle';
    toggleBtn.id = 'sidebar-toggle';
    toggleBtn.type = 'button';
    shell.appendChild(toggleBtn);
  } else if (toggleBtn.parentElement !== shell) {
    shell.appendChild(toggleBtn);
  }

  let copyright = shell.querySelector('.sidebar-copyright') || sidebar.querySelector('.sidebar-copyright');
  if (!copyright) {
    copyright = document.createElement('span');
    copyright.className = 'sidebar-copyright';
    copyright.textContent = '© 2026 Tipatask';
  }
  shell.appendChild(copyright);

  sidebar.querySelectorAll('.sidebar-copyright').forEach(candidate => {
    if (candidate !== copyright) candidate.remove();
  });

  updateSidebarToggle(toggleBtn);
  return toggleBtn;
}

// ── Mount (bind events) ──

export function mountSidebar() {
  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;

  // Toggle collapse
  const toggleBtn = ensureStableSidebarToggle(sidebar);
  if (!toggleBtn) return;
  toggleBtn.addEventListener('click', () => {
    isCollapsed = !isCollapsed;
    localStorage.setItem('tipatask_sidebar_collapsed', isCollapsed);
    sidebar.classList.toggle('collapsed', isCollapsed);
    updateSidebarToggle(toggleBtn);
    closeScopePanel();
  });

  // Workspace scope popover trigger (All Projects / project / + New Project)
  const scopeBtn = document.getElementById('sidebar-scope-btn');
  if (scopeBtn) {
    scopeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleScopePanel();
    });
    scopeBtn.addEventListener('keydown', onScopeButtonKeydown);
  }

  // Close mobile sidebar on nav link click
  sidebar.querySelectorAll('.sidebar-link').forEach(link => {
    link.addEventListener('click', () => { closeMobileSidebar(); });
  });

  // Initial project list fetch
  refreshProjectList();
}

// ── Workspace scope popover (custom listbox, C1335) ──
//
// Replaces the pre-C1335 native <select id="sidebar-scope-select"> — a native <option> can't
// host a button, and each project row now carries its own Open-in-New-Tab control. The panel is
// a position:fixed node appended to document.body (not a child of .sidebar), the same house
// popover idiom used by settings.js's color-swatch picker and sprint-board.js's status dropdown.
// This deliberately avoids the pre-C1268 mistake (see tt-web-sidebar.md): a panel positioned
// relative to .sidebar-scroll gets clipped by its overflow-y:auto.

function scopeLabelText() {
  if (!currentProjectId) return t('sidebar.allProjects');
  return getCachedProjectName(currentProjectId) || t('sidebar.allProjects');
}

function updateScopeLabel() {
  const label = document.getElementById('sidebar-scope-label');
  if (label) label.textContent = scopeLabelText();
}

function buildScopeRowsHtml() {
  const projects = cachedProjects || [];
  const current = currentProjectId ? String(currentProjectId) : ALL_PROJECTS_SCOPE;

  let html = `<div class="sidebar-scope-option" role="option" data-value="${ALL_PROJECTS_SCOPE}"
    aria-selected="${current === ALL_PROJECTS_SCOPE}">
    <span class="sidebar-scope-option-label" data-i18n-text="sidebar.allProjects">${t('sidebar.allProjects')}</span>
  </div>`;

  html += projects.map(p => {
    const name = escapeHtml(p.name);
    return `<div class="sidebar-scope-option" role="option" data-value="${p.id}"
      aria-selected="${current === String(p.id)}">
      <span class="sidebar-scope-option-label">${name}</span>
      <a class="sidebar-scope-option-open-btn" href="#/projects/${p.id}/board"
        target="_blank" rel="noopener" data-i18n-title="sidebar.openTab" title="${t('sidebar.openTab')}" aria-label="${escapeHtml(t('sidebar.openProjectTab', { name: p.name }))}">${ICONS.externalLink}</a>
    </div>`;
  }).join('');

  html += `<div class="sidebar-scope-option" role="option" data-value="${SCOPE_NEW}" aria-selected="false">
    <span class="sidebar-scope-option-label" data-i18n-text="sidebar.newProject">${t('sidebar.newProject')}</span>
  </div>`;

  return html;
}

function selectScope(value) {
  if (value === SCOPE_NEW) {
    openCreateProjectModal();
    return;
  }

  closeMobileSidebar();

  if (value === ALL_PROJECTS_SCOPE) {
    // Canonical target is '#/' (the dashboard), regardless of which project was
    // selected before this pick. '?tab=all' is a forward-compatible deep-link hint
    // for C1276's dashboard All-Tasks tab — router.js strips the query before
    // matching, so this resolves to dashboardPage exactly as '/' does today.
    navigate('/?tab=all');
  } else {
    navigate('/projects/' + value + '/board');
  }
}

function scopeOptionRows() {
  return scopePanel ? Array.from(scopePanel.querySelectorAll('.sidebar-scope-option')) : [];
}

function setScopeActiveIndex(index) {
  const rows = scopeOptionRows();
  if (!rows.length) return;
  scopeActiveIndex = (index + rows.length) % rows.length;
  rows.forEach((row, i) => row.classList.toggle('active', i === scopeActiveIndex));
  rows[scopeActiveIndex].scrollIntoView({ block: 'nearest' });
}

function commitActiveScopeRow() {
  const rows = scopeOptionRows();
  if (scopeActiveIndex < 0 || scopeActiveIndex >= rows.length) return;
  const value = rows[scopeActiveIndex].dataset.value;
  closeScopePanel();
  selectScope(value);
}

function positionScopePanel(btn) {
  if (!scopePanel) return;
  const rect = btn.getBoundingClientRect();
  const panelHeight = scopePanel.offsetHeight;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < panelHeight + 8 && rect.top > panelHeight + 8;
  const top = openAbove ? Math.max(4, rect.top - panelHeight - 4) : rect.bottom + 4;
  const maxLeft = window.innerWidth - scopePanel.offsetWidth - 4;
  scopePanel.style.top = `${top}px`;
  scopePanel.style.left = `${Math.max(4, Math.min(rect.left, maxLeft))}px`;
  scopePanel.style.minWidth = `${rect.width}px`;
}

function detachScopeOutsideHandler() {
  if (scopeOutsideHandler) {
    document.removeEventListener('mousedown', scopeOutsideHandler);
    scopeOutsideHandler = null;
  }
  if (scopeResizeHandler) {
    window.removeEventListener('resize', scopeResizeHandler);
    scopeResizeHandler = null;
  }
  if (scopeScrollHandler) {
    document.removeEventListener('scroll', scopeScrollHandler, true);
    scopeScrollHandler = null;
  }
}

function closeScopePanel() {
  detachScopeOutsideHandler();
  if (scopePanel) {
    scopePanel.remove();
    scopePanel = null;
  }
  scopeActiveIndex = -1;
  const btn = document.getElementById('sidebar-scope-btn');
  if (btn) btn.setAttribute('aria-expanded', 'false');
}

function onScopePanelClick(e) {
  const openBtn = e.target.closest('.sidebar-scope-option-open-btn');
  if (openBtn) {
    // The anchor's own target="_blank" handles the new tab — just don't also run the
    // row's select/navigate below (panel stays open for opening more projects).
    e.stopPropagation();
    return;
  }
  const row = e.target.closest('.sidebar-scope-option');
  if (!row) return;
  const value = row.dataset.value;
  closeScopePanel();
  selectScope(value);
}

function openScopePanel() {
  const btn = document.getElementById('sidebar-scope-btn');
  if (!btn) return;
  closeScopePanel();

  scopePanel = document.createElement('div');
  scopePanel.className = 'sidebar-scope-panel';
  scopePanel.id = 'sidebar-scope-panel';
  scopePanel.setAttribute('role', 'listbox');
  scopePanel.setAttribute('aria-label', t('sidebar.scope'));
  scopePanel.innerHTML = buildScopeRowsHtml();
  scopePanel.addEventListener('click', onScopePanelClick);
  document.body.appendChild(scopePanel);
  positionScopePanel(btn);

  btn.setAttribute('aria-expanded', 'true');

  const rows = scopeOptionRows();
  const selectedIndex = rows.findIndex(row => row.getAttribute('aria-selected') === 'true');
  setScopeActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);

  // Deferred so the click that opened the panel doesn't also close it — same idiom as
  // board-filters.js's sprint dropdown and settings.js's color-swatch picker.
  setTimeout(() => {
    scopeOutsideHandler = (ev) => {
      if (scopePanel && scopePanel.contains(ev.target)) return;
      if (btn.contains(ev.target)) return;
      closeScopePanel();
    };
    document.addEventListener('mousedown', scopeOutsideHandler);

    scopeResizeHandler = () => closeScopePanel();
    window.addEventListener('resize', scopeResizeHandler);

    // Capture phase — .sidebar-scroll scrolling under the fixed panel wouldn't otherwise
    // bubble a scroll event up to document.
    scopeScrollHandler = (ev) => {
      if (scopePanel && scopePanel.contains(ev.target)) return;
      closeScopePanel();
    };
    document.addEventListener('scroll', scopeScrollHandler, true);
  }, 0);
}

function toggleScopePanel() {
  if (scopePanel) {
    closeScopePanel();
  } else {
    openScopePanel();
  }
}

function onScopeButtonKeydown(e) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (!scopePanel) { openScopePanel(); return; }
    setScopeActiveIndex(scopeActiveIndex + 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (scopePanel) setScopeActiveIndex(scopeActiveIndex - 1);
  } else if (e.key === 'Enter') {
    if (scopePanel) {
      e.preventDefault();
      commitActiveScopeRow();
    }
    // else: panel closed — let the button's native click (fired for Enter too) open it.
  } else if (e.key === 'Escape') {
    if (scopePanel) {
      e.preventDefault();
      closeScopePanel();
    }
  }
}

function renderScopeOptions() {
  updateScopeLabel();
  // A project created/selected before its row existed yet (the old C1268 race) can no
  // longer land on the wrong value — rows are always (re)built fresh at open time from
  // cachedProjects. Only refresh in place if the panel happens to be open right now.
  if (scopePanel) {
    // scopePanel's own 'click' listener (bound once in openScopePanel()) survives this
    // innerHTML rewrite — only its children are replaced, not listeners on the panel itself.
    scopePanel.innerHTML = buildScopeRowsHtml();
    setScopeActiveIndex(scopeActiveIndex);
  }
}

// ── Update context ──

export function updateSidebarContext(projectId, projectName, activeView) {
  currentProjectId = projectId;
  currentView = activeView;

  // Update the scope button's label
  updateScopeLabel();

  // Reveal/hide the PROJECT section
  const projectSection = document.getElementById('sidebar-project-section');
  if (projectSection) {
    projectSection.hidden = !projectId;
  }

  const adminLink = document.getElementById('sidebar-link-admin');
  if (adminLink) adminLink.hidden = !isSystemAdmin(getUser());

  // Update project-scoped link hrefs
  const boardLink = document.getElementById('sidebar-link-board');
  const epicsLink = document.getElementById('sidebar-link-epics');
  const backlogLink = document.getElementById('sidebar-link-backlog');
  const planningLink = document.getElementById('sidebar-link-planning');
  const insightsLink = document.getElementById('sidebar-link-insights');
  const serverAgentLink = document.getElementById('sidebar-link-server-agent');
  const settingsLink = document.getElementById('sidebar-link-settings');
  // Every member gets Project Settings: at minimum it holds their own
  // per-project Notifications tab (settingsTabsFor() lands viewers there).
  // The navbar user menu's Settings is account-level (TPT460), so this is the
  // only entry point to a project's settings. Hidden with no project selected.
  if (settingsLink) settingsLink.hidden = !projectId;

  if (projectId) {
    if (boardLink) { boardLink.href = `#/projects/${projectId}/board`; boardLink.classList.remove('disabled'); }
    if (epicsLink) { epicsLink.href = `#/projects/${projectId}/epics`; epicsLink.classList.remove('disabled'); }
    if (backlogLink) { backlogLink.href = `#/projects/${projectId}/backlog`; backlogLink.classList.remove('disabled'); }
    if (planningLink) { planningLink.href = `#/projects/${projectId}/sprint-planning`; planningLink.classList.remove('disabled'); }
    if (insightsLink) { insightsLink.href = `#/projects/${projectId}/insights`; insightsLink.classList.remove('disabled'); }
    if (serverAgentLink) { serverAgentLink.href = `#/projects/${projectId}/server-agent`; serverAgentLink.classList.remove('disabled'); }
    if (settingsLink) { settingsLink.href = `#/projects/${projectId}/settings`; settingsLink.classList.remove('disabled'); }
  } else {
    if (boardLink) { boardLink.removeAttribute('href'); boardLink.classList.add('disabled'); }
    if (epicsLink) { epicsLink.removeAttribute('href'); epicsLink.classList.add('disabled'); }
    if (backlogLink) { backlogLink.removeAttribute('href'); backlogLink.classList.add('disabled'); }
    if (planningLink) { planningLink.removeAttribute('href'); planningLink.classList.add('disabled'); }
    if (insightsLink) { insightsLink.removeAttribute('href'); insightsLink.classList.add('disabled'); }
    if (serverAgentLink) { serverAgentLink.removeAttribute('href'); serverAgentLink.classList.add('disabled'); }
    if (settingsLink) { settingsLink.removeAttribute('href'); settingsLink.classList.add('disabled'); }
  }

  // Highlight active link
  const sidebar = document.getElementById('sidebar');
  if (sidebar) {
    sidebar.querySelectorAll('.sidebar-link').forEach(link => {
      link.classList.toggle('active', link.dataset.view === activeView);
    });
  }
}

// ── Refresh project list ──

export async function refreshProjectList() {
  try {
    const result = await api.get('/projects');
    cachedProjects = result.projects || [];
  } catch {
    cachedProjects = cachedProjects || [];
  }

  renderScopeOptions();
}

// Full cached project object (includes color_scheme) — cheap source for layout.js's
// theme resolution, no extra fetch when refreshProjectList() already ran (C1265).
export function getCachedProject(projectId) {
  if (!cachedProjects) return null;
  return cachedProjects.find(p => String(p.id) === String(projectId)) || null;
}

export function getCachedProjectName(projectId) {
  return getCachedProject(projectId)?.name || null;
}

// ── Mobile ──

export function openMobileSidebar() {
  isMobileOpen = true;
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebar-overlay');
  if (sidebar) sidebar.classList.add('mobile-open');
  if (overlay) overlay.classList.add('active');
}

export function closeMobileSidebar() {
  isMobileOpen = false;
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebar-overlay');
  if (sidebar) sidebar.classList.remove('mobile-open');
  if (overlay) overlay.classList.remove('active');
}

// ── Cleanup ──

export function destroySidebar() {
  isMobileOpen = false;
  closeScopePanel();
}

export function refreshSidebarLocale() {
  translateElements(document.getElementById('sidebar'));
  updateSidebarToggle(document.getElementById('sidebar-toggle'));
  if (scopePanel) scopePanel.setAttribute('aria-label', t('sidebar.scope'));
  renderScopeOptions();
}
